
import { useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import { Calendar, Clock, Play, GitBranch, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import UserSearchInput from "@/components/permissions/UserSearchInput";
import type { User } from "@/components/permissions";
import { useUpdateWorkflow } from "@/hooks/api/mutations/workflows.mutations";
import { toast } from "sonner";
import { showError } from "@/lib/toast";
import type { Workflow } from "@/types/workflow.types";

interface SettingsTabProps {
  workflow: Workflow;
}

export default function SettingsTab({ workflow }: SettingsTabProps) {
  const t = useTranslations("workflows");
  const updateMutation = useUpdateWorkflow();
  const [isEnabled, setIsEnabled] = useState(workflow.isEnabled);
  const [selectedRunAs, setSelectedRunAs] = useState<User | null>(() => workflowRunAsUser(workflow));

  useEffect(() => {
    setIsEnabled(workflow.isEnabled);
  }, [workflow.isEnabled]);

  useEffect(() => {
    setSelectedRunAs(workflowRunAsUser(workflow));
  }, [workflow]);

  const handleToggleEnabled = () => {
    const newValue = !isEnabled;
    setIsEnabled(newValue);
    updateMutation.mutate(
      { id: workflow.id, data: { isEnabled: newValue } },
      {
        onSuccess: () => {
          toast.success(newValue ? t("settings.toast_enabled") : t("settings.toast_disabled"));
        },
        onError: (err) => {
          setIsEnabled(!newValue);
          showError((err as { message?: string }).message || t("settings.toast_update_failed"));
        },
      }
    );
  };

  const handleSelectRunAs = (user: User) => {
    const previous = selectedRunAs;
    setSelectedRunAs(user);
    updateMutation.mutate(
      {
        id: workflow.id,
        data: {
          runAsPrincipalKind: "user",
          runAsPrincipalId: user.id,
        },
      },
      {
        onSuccess: () => {
          toast.success(t("settings.toast_run_as_updated"));
        },
        onError: (err) => {
          setSelectedRunAs(previous);
          showError((err as { message?: string }).message || t("settings.toast_update_failed"));
        },
      },
    );
  };

  const handleClearRunAs = () => {
    if (workflow.scheduleType !== "manual" && isEnabled) {
      showError(t("settings.run_as_required"));
      return;
    }
    const previous = selectedRunAs;
    setSelectedRunAs(null);
    updateMutation.mutate(
      {
        id: workflow.id,
        data: {
          runAsPrincipalKind: null,
          runAsPrincipalId: null,
        },
      },
      {
        onSuccess: () => {
          toast.success(t("settings.toast_run_as_cleared"));
        },
        onError: (err) => {
          setSelectedRunAs(previous);
          showError((err as { message?: string }).message || t("settings.toast_update_failed"));
        },
      },
    );
  };

  const scheduleIcon =
    workflow.scheduleType === "cron"
      ? Calendar
      : workflow.scheduleType === "once"
        ? Clock
        : Play;
  const ScheduleIcon = scheduleIcon;

  return (
    <div className="space-y-4">
      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-4">
          <Section title={t("settings.section_general")}>
            <SettingRow label={t("settings.field_name")} value={workflow.name} />
            <SettingRow label={t("settings.field_slug")} value={workflow.slug} mono />
            {workflow.description && (
              <SettingRow label={t("settings.field_description")} value={workflow.description} />
            )}
          </Section>

          <Section title={t("settings.section_schedule")}>
            <SettingRow
              label={t("settings.field_schedule_type")}
              value={
                <span className="inline-flex items-center gap-1.5">
                  <ScheduleIcon className="size-4 text-[var(--muted-foreground)]" />
                  <span className="capitalize">{workflow.scheduleType}</span>
                </span>
              }
            />
            {workflow.cronExpression && (
              <SettingRow label={t("settings.field_cron_expression")} value={workflow.cronExpression} mono />
            )}
            {workflow.nextRunAt && (
              <SettingRow
                label={t("settings.field_next_run")}
                value={new Date(workflow.nextRunAt).toLocaleString()}
              />
            )}
            {workflow.scheduleType !== "manual" && (
              <SettingRow
                label={t("settings.field_run_as")}
                value={
                  <div className="max-w-md space-y-1.5">
                    <UserSearchInput
                      orgId={workflow.orgId}
                      selectedUser={selectedRunAs}
                      onSelectUser={handleSelectRunAs}
                      onClearSelection={handleClearRunAs}
                      excludeUserIds={new Set()}
                    />
                    <p className="text-xs leading-5 text-[var(--muted-foreground)]">
                      {t("settings.run_as_help")}
                    </p>
                  </div>
                }
              />
            )}
          </Section>

          <Section title={t("settings.section_execution")}>
            <SettingRow
              label={t("settings.field_execution_mode")}
              value={
                <span className="inline-flex items-center gap-1.5">
                  {workflow.executionMode === "dag" ? (
                    <GitBranch className="size-4 text-[var(--muted-foreground)]" />
                  ) : (
                    <Layers className="size-4 text-[var(--muted-foreground)]" />
                  )}
                  <span className="capitalize">{workflow.executionMode}</span>
                </span>
              }
            />
          </Section>
        </div>

        <Section title={t("settings.section_status")}>
          <div className="space-y-3 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-[var(--foreground)]">
                {isEnabled ? t("settings.status_enabled_title") : t("settings.status_disabled_title")}
              </p>
              <p className="mt-1 text-xs leading-5 text-[var(--muted-foreground)]">
                {isEnabled
                  ? t("settings.status_enabled_desc")
                  : t("settings.status_disabled_desc")}
              </p>
            </div>
            <Button
              variant={isEnabled ? "outline" : "primary"}
              size="sm"
              onClick={handleToggleEnabled}
              disabled={updateMutation.isPending}
              className="w-full"
            >
              {isEnabled ? t("settings.btn_disable") : t("settings.btn_enable")}
            </Button>
          </div>
        </Section>
      </div>
    </div>
  );
}

function workflowRunAsUser(workflow: Workflow): User | null {
  if (workflow.runAsPrincipalKind !== "user" || !workflow.runAsPrincipalId) {
    return null;
  }
  return {
    id: workflow.runAsPrincipalId,
    email: workflow.runAsPrincipalUser?.email ?? null,
    username: workflow.runAsPrincipalUser?.username ?? null,
    firstName: workflow.runAsPrincipalUser?.firstName ?? undefined,
    lastName: workflow.runAsPrincipalUser?.lastName ?? undefined,
  };
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--card)]">
      <h3 className="border-b border-[var(--border)] px-4 py-3 text-sm font-semibold text-[var(--foreground)]">
        {title}
      </h3>
      <div className="divide-y divide-[var(--border)]">
        {children}
      </div>
    </section>
  );
}

function SettingRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="grid gap-1 px-4 py-3 sm:grid-cols-[180px_minmax(0,1fr)] sm:items-start">
      <span className="text-sm text-[var(--muted-foreground)]">{label}</span>
      <span
        className={`min-w-0 break-words text-sm font-medium text-[var(--foreground)] ${mono ? "font-mono text-xs" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}
